import {
  InformalFeedbackRequest,
  InformalFeedbackRequestState,
  InformalFeedbackReview,
  InformalFeedbackReviewDismissReason,
  InformalFeedbackReviewScore,
  InformalFeedbackReviewState,
} from 'api/clients/InformalFeedbackRequestsClient'
import Styles from './ReviewerCard.module.scss'
import Avatar from 'common/Avatar/Avatar'
import { Text } from 'blaze-ui/Text/Text'
import { getInitials } from 'utils'
import { useAuth } from 'hooks/UseAuth'
import { ProseDisplay } from 'common/ProseInput/ProseDisplay'
import { RequestCard } from '../RequestCard'
import { useState } from 'react'
import { ReviewerScore } from './ReviewScore'
import { LastViewed } from './LastViewed'
import { PendingRequesterActions } from './PendingRequesterActions'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { RequestAnotherReviewButton } from './RequestAnotherReviewButton'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'

interface Props {
  review: InformalFeedbackReview
  request: InformalFeedbackRequest
  reviewerFieldWidth: number
}

export const ReviewerCard = ({ review, request, reviewerFieldWidth }: Props) => {
  const auth = useAuth()
  const [removed, setRemoved] = useState(false)
  const { isMobile } = useResponsiveDesign()

  const reviewer = review.reviewer
  const reviewerIsMe = auth && reviewer.id === auth.id
  const requesterIsMe = auth && request.requester.id === auth.id
  const lastViewed = request.last_viewed[review.reviewer.id]
  const removedFromReview = review.dismissed_reason === InformalFeedbackReviewDismissReason.RemovedFromReview
  const isReRequest = review.dismissed_reason === InformalFeedbackReviewDismissReason.RequestedAnotherReview
  const showActions =
    request.state === InformalFeedbackRequestState.Open &&
    review.state === InformalFeedbackReviewState.Pending &&
    requesterIsMe
  const showRequestAnotherReviewButton =
    request.state === InformalFeedbackRequestState.Open &&
    review.state === InformalFeedbackReviewState.Submitted &&
    review.score === InformalFeedbackReviewScore.NeedsMajorChanges &&
    requesterIsMe

  if (removed || removedFromReview) return null

  return (
    <RequestCard.Root>
      <RequestCard.Header>
        <div className={Styles.reviewerContainer}>
          <div className={Styles.reviewer}>
            <Avatar url={reviewer.avatar_url} label={getInitials(reviewer.display_name)} size={25} />
            <div className={Styles.reviewerName} style={{ width: reviewerFieldWidth }}>
              <Text color="var(--dark-80)" variant="secondary" lineClamp={1}>
                {reviewerIsMe ? 'You' : reviewer.display_name}
              </Text>
            </div>
          </div>
          {review.state === InformalFeedbackReviewState.Pending ? (
            <LastViewed lastViewed={lastViewed} />
          ) : (
            <>
              <ReviewerScore review={review} />
              <ResponsiveDesign.Desktop>
                {isReRequest && (
                  <div className={Styles.anotherRequestLabel}>
                    <Text variant="metadata" color="var(--dark-60)">
                      Another Review Requested
                    </Text>
                  </div>
                )}
              </ResponsiveDesign.Desktop>
            </>
          )}
        </div>
        {showActions && <PendingRequesterActions request={request} review={review} setRemoved={setRemoved} />}
        {showRequestAnotherReviewButton && !isMobile && (
          <RequestAnotherReviewButton request={request} size="xs" variant="ghost" />
        )}
      </RequestCard.Header>
      {review.message && (
        <RequestCard.Message>
          <ProseDisplay html={review.message} />
        </RequestCard.Message>
      )}
      {showRequestAnotherReviewButton && isMobile && (
        <RequestAnotherReviewButton request={request} size="sm" variant="secondary" />
      )}
      <ResponsiveDesign.Mobile>
        {isReRequest && (
          <div className={Styles.anotherRequestLabel}>
            <Text variant="metadata" color="var(--dark-60)">
              Another Review Requested
            </Text>
          </div>
        )}
      </ResponsiveDesign.Mobile>
    </RequestCard.Root>
  )
}
